import {
  Tab,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  createStyles,
  makeStyles,
  useMediaQuery,
  withStyles,
} from "@material-ui/core";
import React, { useMemo, useState } from "react";

import classnames from "classnames";
import { get } from "lodash";
import moment from "moment";
import { useFormState } from "react-final-form";
import { useLocation } from "react-router-dom";
import { useTranslate } from "react-admin";
import { Group, GroupTitle } from "ra-friendsofbabba";

const YEARS = [1, 2, 3];
const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const useStyles = makeStyles((theme) =>
  createStyles({
    container: {
      "& h6": {
        marginTop: theme.spacing(3),
      },
      "& h6, & .MuiTypography-body1": {
        paddingTop: theme.spacing(1),
        paddingLeft: 16,
        paddingBottom: theme.spacing(1),
      },
      "& .MuiTypography-body1": {
        marginBottom: theme.spacing(2),
      },
      marginBottom: theme.spacing(3),
    },
    tabs: {
      marginLeft: 1,
    },
    table: {
      width: "100%",
      overflow: "hidden",
      [theme.breakpoints.down("xs")]: {
        "& *": {
          fontSize: "10px",
          padding: 0,
          lineHeight: "12px",
        },
      },
      "& .MuiTypography-body1": {
        fontSize: "14px",
        lineHeight: "16px",
      },
    },
    cell: {
      width: 20,
      height: 30,
      textAlign: "center",
      padding: 0,
      border: 1,
      fontSize: 14,
      borderStyle: "solid",
      borderColor: theme.palette.secondary.main,
    },
    label: {
      textAlign: "left",
      paddingLeft: 2,
    },
    filled: {
      backgroundColor: "white",
    },
    checked: {
      backgroundColor: theme.palette.secondary.main,
    },
    checkedSecondary: {
      backgroundColor: theme.palette.secondary.light,
    },
    help: {
      padding: theme.spacing(2),
    },
  })
);

const StyledTableCellHead = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    border: 1,
    borderStyle: "solid",
    borderColor: theme.palette.common.white,
  },
}))(TableCell);

const StyledTableCellBody = withStyles((theme) => ({
  body: {
    border: "1px solid white",
  },
}))(TableCell);

const StyledTableCellFooter = withStyles((theme) => ({
  footer: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    border: "1px solid white",
  },
}))(TableCell);

const isMonthChecked = (startDate, duration, month) => {
  let start = moment(startDate);
  const end = start.clone().add(duration, "month");
  const grid = [];
  while (start < end) {
    grid.push(start.format("YYYY-MM"));
    start = start.add(1, "month");
  }
  return grid.some((g) => g === month);
};

const monthRelease = (workPackages, month, list) => {
  if (!workPackages || workPackages.length === 0) {
    return "";
  }

  let items = workPackages.map((wp) =>
    wp[list]
      .filter((item) => !item.type || item.type === "original")
      .map((item) => ({
        wp_number: wp.order_index,
        ...item,
      }))
  );
  items = [].concat.apply([], items); // flat
  items = items.filter((m) => {
    return moment(m.release_date, "YYYY-MM-DD").format("YYYY-MM") === month;
  });

  return items.length > 0
    ? items.map((item) => `${item.wp_number}.${item.order_index + 1} `)
    : "";
};

const getYears = (workPackages) => {
  const startingDates = workPackages.map((wp) => moment(wp.start_date));
  const firstStartingDate = moment.min(startingDates);

  let years = {};
  let maxDate = firstStartingDate.clone().add(36, "months");
  let dateCursor = firstStartingDate.clone();
  let monthIndex = 0;
  while (dateCursor < maxDate) {
    const yearIndex = parseInt(monthIndex / 12);
    const v = dateCursor.format("YYYY-MM");
    if (!years[yearIndex]) {
      years[yearIndex] = [];
    }

    years[yearIndex].push(v);

    dateCursor = dateCursor.add(1, "month");
    monthIndex++;
  }
  return years;
};

const getModel = (workPackages, selectedYear = null, translate) => {
  const years = getYears(workPackages);
  const filteredYears = Object.keys(years).filter(
    (y) => selectedYear === null || parseInt(y, 10) === selectedYear - 1
  );
  const wps = workPackages.map((wp) => ({
    number: wp.order_index,
    years: filteredYears.map((year) =>
      years[year].map((month) => ({
        checked: isMonthChecked(wp.start_date, wp.duration, month),
      }))
    ),
    activities: wp.activities
      .filter((a) => a.type === "original")
      .map((a) => ({
        activity: translate("app.activity", {
          activity: `${wp.order_index}.${a.order_index + 1}`,
        }),
        years: filteredYears.map((year) =>
          years[year].map((month) => ({
            checked: isMonthChecked(a.start_date, a.duration, month),
          }))
        ),
      })),
  }));

  const deliverables = filteredYears.map((year) =>
    years[year].map((month) => ({
      deliverable: monthRelease(workPackages, month, "deliverables"),
    }))
  );

  const milestones = filteredYears.map((year) =>
    years[year].map((month) => ({
      milestone: monthRelease(workPackages, month, "milestones"),
    }))
  );

  return {
    workPackages: wps,
    deliverables,
    milestones,
  };
};

const TimeScheduleField = ({ source }) => {
  const { pathname } = useLocation();
  const classes = useStyles();
  const [year, setYear] = useState(1);
  const translate = useTranslate();
  const formState = useFormState();

  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const years = useMemo(() => (isSmall ? [year] : YEARS), [isSmall, year]);
  const wps = get(formState.values, source, []).filter(
    (wp) => wp.type === "original"
  );
  const model = useMemo(() => {
    return getModel(wps, isSmall ? year : null, translate);
  }, [wps, year, translate, isSmall]);
  const handleYearChange = (_, year) => setYear(year + 1);
  if (!pathname.endsWith("/2")) {
    // In questo modo ottimizzo il rendering evitando di ridisegnare il cronoprogramma
    // fino a quando non è selezionato il tab in cui è contenuto, cioè il numero 2.
    return null;
  }

  return (
    <Group wrapper>
      <GroupTitle
        title={translate("resources.executive-projects.titles.time_schedule")}
        subTitle={translate(
          "resources.executive-projects.messages.time_schedule"
        )}
      />

      {isSmall && (
        <Tabs
          className={classes.tabs}
          value={year - 1}
          onChange={handleYearChange}
          indicatorColor="secondary"
          textColor="secondary"
        >
          {YEARS.map((year) => (
            <Tab key={year} label={translate("app.year_number", { year })} />
          ))}
        </Tabs>
      )}

      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <StyledTableCellHead rowSpan={2} />
            {years.map((year) => (
              <StyledTableCellHead
                key={`${year}-number`}
                colSpan={12}
                className={classes.cell}
              >
                {translate("app.year_number", { year })}
              </StyledTableCellHead>
            ))}
          </TableRow>
          <TableRow>
            {years.map((year) => (
              <React.Fragment key={`${year}-semesters`}>
                <StyledTableCellHead colSpan={6} className={classes.cell}>
                  {translate("app.first_semester")}
                </StyledTableCellHead>

                <StyledTableCellHead colSpan={6} className={classes.cell}>
                  {translate("app.second_semester")}
                </StyledTableCellHead>
              </React.Fragment>
            ))}
          </TableRow>
          <TableRow>
            <StyledTableCellHead className={classes.cell}>
              {translate("app.project_duration_months")}
            </StyledTableCellHead>
            {years.map((year) => {
              return MONTHS.map((month) => (
                <StyledTableCellHead
                  key={`${year}-${month}-number`}
                  className={classes.cell}
                >
                  {month}
                </StyledTableCellHead>
              ));
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {model.workPackages.map((wp) => (
            <React.Fragment key={`${wp.number}-sf`}>
              <TableRow>
                <StyledTableCellBody
                  className={classnames(classes.cell, classes.label)}
                >
                  WP {wp.number}
                </StyledTableCellBody>
                {wp.years.map((months, index) =>
                  months.map((month, index2) => (
                    <StyledTableCellBody
                      key={`${wp.number}-${index}-${index2}wp`}
                      className={classnames(
                        classes.cell,
                        month.checked && classes.checked
                      )}
                    />
                  ))
                )}
              </TableRow>
              {wp.activities.map((a, index) => (
                <TableRow key={`${a.activity}-${index}-activity`}>
                  <StyledTableCellBody
                    className={classnames(classes.cell, classes.label)}
                  >
                    {a.activity}
                  </StyledTableCellBody>
                  {a.years.map((months, index) =>
                    months.map((month, index2) => (
                      <StyledTableCellBody
                        key={`${a.activity}-${index}-${index2}`}
                        className={classnames(
                          classes.cell,
                          month.checked && classes.checkedSecondary
                        )}
                      />
                    ))
                  )}
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <StyledTableCellFooter className={classes.cell}>
              Deliverables
            </StyledTableCellFooter>
            {model.deliverables.map((years, index) =>
              years.map((month, index2) => (
                <StyledTableCellFooter
                  key={`${index}-${index2}-deliverables`}
                  className={classes.cell}
                >
                  {month.deliverable}
                </StyledTableCellFooter>
              ))
            )}
          </TableRow>
          <TableRow>
            <StyledTableCellFooter className={classes.cell}>
              Milestones
            </StyledTableCellFooter>
            {model.milestones.map((years, index) =>
              years.map((month, index2) => (
                <StyledTableCellFooter
                  key={`${index}-${index2}-milestones`}
                  className={classes.cell}
                >
                  {month.milestone}
                </StyledTableCellFooter>
              ))
            )}
          </TableRow>
        </TableFooter>
      </Table>
      {model.workPackages.length === 0 && (
        <Typography className={classes.help}>
          {translate("app.label.no_work_packages")}
        </Typography>
      )}
    </Group>
  );
};

export default TimeScheduleField;
